(ns minorodata.components.sidebar.global-search
  (:require
   ["lucide-react" :refer [BrushCleaningIcon GitGraphIcon Search]]
   [applied-science.js-interop :as j]
   [clojure.string :as cstr]
   [goog.functions :refer [debounce]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
   [minorodata.components.shortcut.core :as c.shortcut]
   [minorodata.components.spinner.core :refer [spinner-lucide]]
   [minorodata.global-search.api :refer [min-query-length]]
   [re-frame.core :as re-frame]
   [react :as react]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :as button]
   [ui.fields :refer [input]]
   [ui.popover :refer [popover-content popover-root popover-trigger]]
   [ui.tabs :as tabs]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))


(def search-debounce-ms 300)
(def filterable-types ["all" "group" "transform" "source" "extract" "webform"])
(def all-types (set filterable-types))

(defn run-search
  "Fire the search immediately. `types` is a set of entity-type strings. A full
  selection (every chip on) is treated as no filter, so the api layer omits
  `:types` and searches everything."
  [query types]
  (dispatch [:global-search.api/fetch
             {:query query
              :types (when-not (= types all-types) types)}]))

(defonce ^:private last-searched-query (atom nil))

(defn- search-and-track [query types]
  (when (and (>= (count query) min-query-length)
             (not (cstr/blank? query))
             (not= (cstr/trim query) (some-> @last-searched-query cstr/trim)))
    (dispatch [:posthog/capture {:event (:global-search/performed posthog-events/events)
                                 :props {:type (if (= types all-types) "all" (first types))}}]))
  (reset! last-searched-query query)
  (run-search query types))

(def dispatch-search
  (debounce search-and-track search-debounce-ms))


(def ^:private all-tab-value "all")

;; Icons + brand colours per entity type come from the shared `tab-icon`
;; (minorodata.components.pipeline-editor.utils); this just supplies display labels.
(def type->label
  {all-tab-value "All"
   "transform"   "Transform"
   "group"       "Group"
   "source"      "Source"
   "extract"     "Extract"
   "webform"     "Webform"})



(defn type-tabs
  "Single-select tabs that scope search to one entity type — or `All`. Picking a
  tab re-runs the search immediately against the current term. Selection is held
  as a type set so the api layer is unchanged: `All` is the full set, a scoped
  tab is a singleton. `on-select` receives the next set.

  Built on the `ui.tabs` composites: the set-based selection maps onto Radix's
  single-string value via `all`/singleton-type, converted back in
  `on-value-change`."
  [{:keys [selected on-select]}]
  (let [all-active? (= selected all-types)
        value       (if all-active? all-tab-value (first selected))]
    [tabs/tabs-root
     {:value           value
      :on-value-change (fn [v]
                         (on-select (if (= v all-tab-value) all-types #{v})))}
     [tabs/tabs-list {:variant :underlined :size :small :class "px-2"}
      (for [type filterable-types
            :let [active?  (= selected #{type})
                  colored? (or all-active? active?)]]
        ^{:key type}
        [tabs/tabs-trigger {:value   type
                            :variant :underlined
                            :size    :small
                            :data-cy (str "global-search__type-" type)
                            :class   "gap-1.5"}
         [tab-icon {:type type :size 14 :muted? (not colored?) :class "shrink-0"}]
         (type->label type)])]]))


(defn result-href
  "Build a route href for a search result, or nil when the result lacks the
  params its route needs."
  [{:keys [type id name metadata]}]
  (let [{:keys [group-id source-id]} metadata]
    (case type
      "transform" (when group-id
                    ;; open the transform's settings panel within its group
                    (rt.easy/href :transforms
                                  {:group-id group-id}
                                  {:transform-settings true :transform-id id}))
      "group"     (rt.easy/href :group-details {:group-id id})
      "source"    (rt.easy/href :source-details {:source-id id})
      "extract"   (when source-id
                    (rt.easy/href :extract-details {:source-id source-id :extract-id id}))
      "webform"   (rt.easy/href :webforms-table {:table-name name})
      nil)))


(defn result-subtitle
  "Muted second-row text: transforms/sources show their location breadcrumb
  (source / group); extracts show their parent source; everything else falls
  back to the description."
  [{:keys [type description metadata]}]
  (case type
    ("transform" "extract") (let [{:keys [source-name group-name]} metadata]
                              (->> [source-name group-name]
                                   (remove cstr/blank?)
                                   (cstr/join " / ")
                                   not-empty))
    description))


(defn pipeline-editor-button
  "Standalone action shown on transform / extract results — opens the entity as
  a tab in the Pipeline Editor. Stops the click from also triggering the row's
  details navigation."
  [result on-navigate]
  [:button {:type     "button"
            :title    "Open in Pipeline Editor"
            :data-cy  :global-search__open-in-pe
            :on-click (fn [e]
                        (.preventDefault e)
                        (.stopPropagation e)
                        (dispatch [:global-search/record-recent result])
                        (dispatch [:global-search/open-in-pipeline-editor result])
                        (when on-navigate (on-navigate)))
            :class    "shrink-0 rounded-r-md px-3 transition-colors hover:bg-kleene-hover-background text-kleene-text-secondary"}
   [:> GitGraphIcon {:size 16}]])


(defn result-item [{:keys [type name] :as result} on-navigate]
  (let [href     (result-href result)
        subtitle (result-subtitle result)
        tag      (if href :a :div)
        pe?      (contains? #{"transform" "extract"} type)]
    [:div {:class (clsx "flex min-w-0 items-stretch gap-1 rounded-md")}
     [tag {:href     href
           :on-click (when href
                       (fn []
                         (dispatch [:global-search/record-recent result])
                         (when on-navigate (on-navigate))))
           :class    (clsx "flex min-w-0 flex-1 items-center gap-2 rounded-l-md p-2 text-sm transition-colors hover:bg-kleene-hover-background"
                           {"cursor-pointer" href
                            "rounded-md" (#{"source" "group" "webform"} type)})}
      [tab-icon {:type type :size 16 :class "shrink-0"}]
      [:div {:class "min-w-0 flex flex-col"}
       [:span {:class "truncate text-xs font-medium leading-3 text-kleene-text"} name]
       (when (not-empty subtitle)
         [:span {:class "truncate text-xs leading-3 text-kleene-dark-05"} subtitle])]]
     (when pe?
       [pipeline-editor-button result on-navigate])]))


(defn- search-prompt
  "The idle 'start typing' hint. Full-height (vertically centred) on its own;
  `compact?` drops the min-height so it can sit under the recent list."
  [{:keys [class]}]
  [:div {:class (clsx "flex flex-col items-center justify-center gap-2 px-4 py-6 text-center text-sm text-kleene-dark-05" class)}
   [:> Search {:size 24 :class "opacity-40"}]
   [:span "Start typing to search across Kleene"]])


(defn search-results [{:keys [term on-navigate]}]
  (let [loading? @(re-frame/subscribe [:mold-http/loading? :global-search.api/fetch])
        results  @(re-frame/subscribe [:global-search/results])
        recent   @(re-frame/subscribe [:global-search/recent])
        term     (some-> term not-empty)]
    (cond
      (not (>= (count term) 3))
      [:div {:class "flex-1 flex flex-col"}
       (when (seq recent)
         [:div {:class "flex flex-col gap-0.5 py-1 px-2 border-b border-kleene-text/10"}
          [:div {:class "flex items-center justify-between gap-2"}
           [typography/h4 "Recent"]
           [button/button {:variant  :empty-transparent
                           :size     :micro
                           :icon     BrushCleaningIcon
                           :class    "text-kleene-text-secondary"
                           :data-cy  :global-search__clear-recent
                           :on-click (fn [] (dispatch [:global-search/clear-recent]))}
            "Clear"]]
          (for [{:keys [type id] :as result} recent]
            ^{:key (str "recent-" type "-" id)}
            [result-item result on-navigate])])
       [search-prompt {:class "flex-1"}]]

      (and loading? (empty? results))
      [:div {:class "min-h-96 flex items-center justify-center gap-2 text-sm text-kleene-dark-05"}
       [spinner-lucide] "Searching…"]

      (empty? results)
      [:div {:class "min-h-96 flex flex-col items-center justify-center gap-2 px-4 py-6 text-center text-sm text-kleene-dark-05"}
       [:> Search {:size 24 :class "opacity-40"}]
       [:span "No results for "
        [:span {:class "font-medium text-kleene-text"} (str "\"" term "\"")]]]

      :else
      [:div {:class "flex flex-col gap-0.5 py-1 px-2"}
       [:div {:class "flex items-center gap-2"}
        [typography/h4 "Search results"]
        (when loading?
          [spinner-lucide {:size 14}])]
       (for [{:keys [type id] :as result} results]
         ^{:key (str type "-" id)}
         [result-item result (fn []
                               (dispatch [:posthog/capture {:event (:global-search/result-selected posthog-events/events)
                                                            :props {:type type}}])
                               (when on-navigate (on-navigate)))])])))


(defn search-popover-content [{:keys [term set-term! selected-types on-select-types on-navigate]}]
  [:div {:class "w-2xl flex flex-col gap-2 min-h-120 max-h-160 overflow-y-auto rounded-xl"}
   [:div {:class "sticky bg-kleene-background top-0 z-10 pt-2 flex flex-col gap-0"}
    [input {:auto-focus  true
            :full-width  true
            :icon        Search
            :data-cy     :global-search__input
            :placeholder "Search across Kleene…"
            ;; borderless: drop the resting/focus ring and let the popover bg show through
            :input-props {:class "ring-0! focus:ring-0! bg-transparent dark:bg-transparent"}
            :value       term
            :on-change   (fn [e]
                           (let [value (j/get-in e [:target :value])]
                             (set-term! value)
                             (dispatch-search value selected-types)))}]
    [type-tabs {:selected selected-types :on-select on-select-types}]]
   [search-results {:term term :on-navigate on-navigate}]])


(defn global-search-node
  "A semitransparent, input-looking node for the sidebar. Clicking it (or
  pressing `/`) opens a popover to the right containing the global search.

  Rendered with `:f>` so it can use React hooks for local state."
  []
  (let [[open? set-open!]                   (react/useState false)
        [term set-term!]                    (react/useState "")
        [selected-types set-selected-types!] (react/useState all-types)
        reset!         (fn []
                         (set-term! "")
                         (set-selected-types! all-types)
                         (cljs.core/reset! last-searched-query nil)
                         (dispatch [:global-search/clear]))
        open           (fn []
                         (dispatch [:global-search/load-recent])
                         (set-open! true))
        on-navigate    (fn []
                         (set-open! false)
                         (reset!))
        on-select-types (fn [types]
                          (set-selected-types! types)
                          (run-search term types))]
    [popover-root {:open           open?
                   :modal          true
                   :on-open-change (fn [next]
                                     (set-open! next)
                                     (if next
                                       (dispatch [:global-search/load-recent])
                                       (reset!)))}
     [popover-trigger {:as-child true}
      [:button {:type    "button"
                :data-cy :global-search__toggle
                :class   (clsx "group flex w-full items-center gap-2 rounded-md px-2.5 py-1.5"
                               "bg-kleene-text/5 ring-1 ring-inset ring-kleene-text/10"
                               "text-sm text-kleene-text/60 transition-colors"
                               "hover:bg-kleene-text/10 hover:text-kleene-text/80"
                               "focus:outline-none focus:ring-2 focus:ring-kleene-secondary-03")}
       [:> Search {:size 16 :class "shrink-0 opacity-70"}]
       [:span {:class "flex-1 text-left truncate"} "Search"]
       [c.shortcut/shortcut
        {:title     "Press / to search"
         :value     "/"
         :shortcuts [{:shortcut "/" :handler open :options {:useKey true}}]
         :classes   {:kbd "border-0! bg-kleene-text/10 h-5 w-5"}}]]]

     [popover-content {:side       "right"
                       :align      "start"
                       :sideOffset 12
                       :class "rounded-xl p-0"}
      [search-popover-content {:term            term
                               :set-term!       set-term!
                               :selected-types  selected-types
                               :on-select-types on-select-types
                               :on-navigate     on-navigate}]]]))
